import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
	BulkAccessAction,
	BulkAccessChange,
	BulkAccessColumn,
	BulkAccessConfirm,
	BulkAccessConfirmPhase,
	BulkAccessControl,
	BulkAccessDataSource,
	BulkAccessId,
	BulkAccessLabels,
	BulkAccessPhrases,
	BulkAccessResult
} from './bulk-access.models';
import { BulkAccessAddSubmit } from './bulk-access-add-dialog/bulk-access-add-dialog.component';
import { PendingChange, PendingChangesService } from '../../../services/pending-changes.service';

let nextInstanceId = 0;

const DEFAULT_PHRASES: BulkAccessPhrases = {
	add: (n, noun) => `Add access for ${n} ${noun}`,
	remove: (n, noun) => `Remove access for ${n} ${noun}`,
	added: (n, noun) => `Added access for ${n} ${noun}`,
	removed: (n, noun) => `Removed access for ${n} ${noun}`
};

/**
 * DOVE-2626 — reusable access-tab pattern, piloted on Item → Insurance Access.
 *
 * - Checkbox column; any selection shows the bulk bar and disables every row trash icon, so
 *   removal only happens one way while a selection is active.
 * - The header checkbox selects the current page only; "Select all N matching" is a separate,
 *   deliberate click that reaches every row matching the active filters across pages.
 * - Filtering is done here (not by p-table) so "matching" is an exact set; changing or
 *   clearing any filter drops the selection, since select-all is scoped to the filter.
 * - Remove (bulk or a single trash icon) and large Adds go through a count-and-scope confirm.
 * - After each batch the list is reloaded quietly: rows stay on screen under the table's
 *   loading overlay and the current page is kept.
 *
 * DOVE-2676 — undo is a delayed send. Accepting a confirm of 2+ rows starts a countdown in the
 * dialog (Cancel hidden, the accept button becomes Undo); nothing is sent until it ends, and Undo
 * just closes the dialog. While it runs the change is registered with PendingChangesService, so
 * leaving the page is stopped first and, if the user leaves anyway, nothing is sent. A single row
 * (trash icon, or Remove Selected with one row) and small Adds are sent straight away, with no undo.
 */
@Component({
	selector: 'app-bulk-access',
	templateUrl: './bulk-access.component.html',
	// Own MessageService so the result toasts never reach (or are reached by) the host page's
	// keyless toasts / p-messages.
	providers: [MessageService]
})
export class BulkAccessComponent<T extends Record<string, any>> implements OnInit, OnChanges, OnDestroy {
	@Input({ required: true }) dataSource!: BulkAccessDataSource<T>;
	@Input({ required: true }) labels!: BulkAccessLabels;
	@Input({ required: true }) columns!: BulkAccessColumn[];
	/** Columns of the Add dialog; defaults to the access table's. */
	@Input() addColumns?: BulkAccessColumn[];
	@Input() idField = 'id';
	@Input() nameField = 'name';
	@Input() rowsPerPageOptions = [10, 25, 50];
	/** Batches at or below this size are named in the confirm; Add confirms above it. */
	@Input() listLimit = 10;
	/** Length of the undo countdown, in seconds. */
	@Input() undoSeconds = 5;

	/** Permission checks: hide the control, or show it disabled with `disabledTooltip`. */
	@Input() addControl: BulkAccessControl = 'enabled';
	@Input() removeControl: BulkAccessControl = 'enabled';
	@Input() disabledTooltip = "You don't have permission to do this.";
	/** No Add, no Remove, no checkboxes (e.g. a list that's maintained automatically). */
	@Input() readOnly = false;
	/** Shown above a read-only table, explaining why it can't be edited. */
	@Input() readOnlyMessage?: string;
	/** Rows that can never be removed (e.g. an insurance's primary branch): no checkbox, no trash. */
	@Input() isRemovable?: (row: T) => boolean;
	/** Tooltip on the disabled trash icon of a row that can't be removed. */
	@Input() notRemovableTooltip?: (row: T) => string;

	@Output() changed = new EventEmitter<BulkAccessChange>();

	readonly toastKey = `bulk-access-${++nextInstanceId}`;

	rows: T[] = [];
	filteredRows: T[] = [];
	filters: Record<string, string> = {};
	selection: T[] = [];
	allMatchingSelected = false;

	first = 0;
	pageSize = 10;

	initialLoading = true;
	busy = false;

	confirm: BulkAccessConfirm<T> | null = null;
	confirmPhase: BulkAccessConfirmPhase = 'confirm';
	confirmError = '';
	secondsLeft = 0;

	leavePromptVisible = false;

	addDialogVisible = false;
	availableRows: T[] = [];
	availableLoading = false;

	/** Passed to p-table so the header checkbox and select-all skip rows that can't be removed. */
	readonly rowSelectable = ({ data }: { data: any }): boolean => this.canRemoveRow(data);

	private countdownTimer: ReturnType<typeof setInterval> | null = null;
	private releasePending: (() => void) | null = null;
	private resolveLeave: ((leave: boolean) => void) | null = null;
	private readonly pendingChange: PendingChange = {
		confirmLeave: () => this.promptLeave(),
		discard: () => this.discardPending()
	};
	private readonly subscriptions = new Subscription();

	constructor(
		private messageService: MessageService,
		private pendingChanges: PendingChangesService
	) {}

	ngOnInit(): void {
		this.pageSize = this.rowsPerPageOptions[0] ?? 10;
		this.reload();
	}

	ngOnChanges(changes: SimpleChanges): void {
		// Same tab, different owner (e.g. navigating item → item): start over against the new source.
		if (changes['dataSource'] && !changes['dataSource'].firstChange) {
			this.discardPending();
			this.rows = [];
			this.filteredRows = [];
			this.filters = {};
			this.first = 0;
			this.clearSelection();
			this.addDialogVisible = false;
			this.messageService.clear(this.toastKey);
			this.initialLoading = true;
			this.reload();
		}
	}

	ngOnDestroy(): void {
		// Leaving mid-countdown sends nothing.
		this.discardPending();
		this.subscriptions.unsubscribe();
	}

	// ── Derived state ────────────────────────────────────────────────────────────

	get canAdd(): boolean {
		return !this.readOnly && this.addControl !== 'hidden';
	}

	/** Checkbox column, bulk bar and trash icons. */
	get canRemove(): boolean {
		return !this.readOnly && this.removeControl !== 'hidden';
	}

	get addDisabled(): boolean {
		return this.addControl === 'disabled';
	}

	get removeDisabled(): boolean {
		return this.removeControl === 'disabled';
	}

	get hasSelection(): boolean {
		return this.selection.length > 0;
	}

	/** Rows matching the filters that can be selected (rows that can't be removed are skipped). */
	get matchingCount(): number {
		return this.filteredRows.filter((row) => this.canRemoveRow(row)).length;
	}

	/** Rows on the visible page, in display order (p-table sorts filteredRows in place). */
	get pageRows(): T[] {
		return this.filteredRows.slice(this.first, this.first + this.pageSize);
	}

	get isPageFullySelected(): boolean {
		const page = this.pageRows.filter((row) => this.canRemoveRow(row));
		return page.length > 0 && page.every((row) => this.isSelected(row));
	}

	get canSelectAllMatching(): boolean {
		return !this.allMatchingSelected && this.isPageFullySelected && this.selection.length < this.matchingCount;
	}

	get selectionSummary(): string {
		const n = this.selection.length;
		const pageSelectable = this.pageRows.filter((row) => this.canRemoveRow(row)).length;
		const onlyThisPage = this.isPageFullySelected && n === pageSelectable && n < this.matchingCount;
		return onlyThisPage ? `${n} selected on this page` : `${n} selected`;
	}

	get addColumnsResolved(): BulkAccessColumn[] {
		return this.addColumns ?? this.columns;
	}

	get countdownNote(): string {
		if (!this.confirm) return '';
		const verb = this.confirm.action === 'add' ? 'Adding' : 'Removing';
		return `${verb} in ${this.secondsLeft}s`;
	}

	get sendingLabel(): string {
		return this.confirm?.action === 'add' ? 'Adding…' : 'Removing…';
	}

	get leaveMessage(): string {
		if (!this.confirm) return '';
		return `${this.phrase(this.confirm.action, this.confirm.rows.length)} hasn't been sent yet. If you leave, nothing will be changed.`;
	}

	canRemoveRow(row: T): boolean {
		return !this.isRemovable || this.isRemovable(row);
	}

	trashTooltip(row: T): string {
		if (this.removeDisabled) return this.disabledTooltip;
		if (!this.canRemoveRow(row)) return this.notRemovableTooltip?.(row) ?? '';
		return this.hasSelection ? 'Use Remove Selected, or clear the selection' : '';
	}

	cellText(row: T, col: BulkAccessColumn): string {
		const value = row[col.field];
		const text = col.format ? col.format(value, row) : value;
		return text == null ? '' : String(text);
	}

	// ── Selection & filters ──────────────────────────────────────────────────────

	onSelectionChange(selection: T[]): void {
		this.selection = selection.filter((row) => this.canRemoveRow(row));
		// Any manual toggle leaves "all matching" mode; the bar falls back to a plain count.
		this.allMatchingSelected = false;
	}

	selectAllMatching(): void {
		this.selection = this.filteredRows.filter((row) => this.canRemoveRow(row));
		this.allMatchingSelected = true;
	}

	clearSelection(): void {
		this.selection = [];
		this.allMatchingSelected = false;
	}

	onFilter(field: string, value: string): void {
		this.filters = { ...this.filters, [field]: value };
		this.applyFilters();
		this.first = 0;
		this.clearSelection();
	}

	onPage(event: { first: number; rows: number }): void {
		this.first = event.first;
		this.pageSize = event.rows;
	}

	// ── Remove ───────────────────────────────────────────────────────────────────

	removeSelected(): void {
		if (this.removeDisabled) return;
		this.openConfirm('remove', this.selection, this.allMatchingSelected);
	}

	removeRow(row: T): void {
		if (this.removeDisabled || !this.canRemoveRow(row)) return;
		this.openConfirm('remove', [row], false);
	}

	// ── Add ──────────────────────────────────────────────────────────────────────

	openAddDialog(): void {
		if (this.addDisabled) return;
		this.addDialogVisible = true;
		this.availableLoading = true;
		this.availableRows = [];
		this.subscriptions.add(
			this.dataSource
				.listAvailable()
				.pipe(finalize(() => (this.availableLoading = false)))
				.subscribe({
					next: (rows) => (this.availableRows = rows),
					error: () => this.toastError(`Failed to load ${this.labels.plural}.`)
				})
		);
	}

	onAddSubmitted(event: BulkAccessAddSubmit<T>): void {
		if (event.rows.length > this.listLimit) {
			this.openConfirm('add', event.rows, event.allMatching);
			return;
		}
		// Small adds have no confirm and no undo: a wrong add is fixed by removing the rows.
		this.runBatch('add', this.idsOf(event.rows));
	}

	// ── Confirm, countdown & undo ────────────────────────────────────────────────

	onConfirmAccept(): void {
		if (!this.confirm || this.confirmPhase !== 'confirm') return;
		if (this.confirm.undoable) {
			this.startCountdown();
		} else {
			this.sendConfirmed();
		}
	}

	onConfirmCancel(): void {
		// Only before the countdown, or after a failure; the countdown has no Cancel (Undo instead).
		if (this.confirmPhase === 'confirm' || this.confirmPhase === 'failed') this.closeConfirm();
	}

	/** Undo during the countdown: nothing was sent, so just close. The selection is kept. */
	undo(): void {
		if (this.confirmPhase !== 'countdown') return;
		this.stopCountdown();
		this.closeConfirm();
	}

	onConfirmRetry(): void {
		if (this.confirmPhase === 'failed') this.sendConfirmed();
	}

	onLeaveStay(): void {
		this.answerLeave(false);
	}

	onLeaveConfirmed(): void {
		this.discardPending();
		this.answerLeave(true);
	}

	describe(row: T): string {
		const name = row[this.nameField];
		return name ? `${row[this.idField]}  ·  ${name}` : `${row[this.idField]}`;
	}

	// ── Internals ────────────────────────────────────────────────────────────────

	private openConfirm(action: BulkAccessAction, rows: T[], allMatching: boolean): void {
		if (!rows.length) return;
		const n = rows.length;
		const noun = this.noun(n);
		const small = n <= this.listLimit;
		this.confirmPhase = 'confirm';
		this.confirmError = '';

		if (action === 'remove') {
			this.confirm = {
				action,
				rows: [...rows],
				header: 'Confirm Removal',
				message:
					`${this.phrase('remove', n)}?` +
					(allMatching && !small
						? ' This matches everything in your current filter, including results not shown on this page.'
						: ''),
				items: small ? rows.map((row) => this.describe(row)) : [],
				acceptLabel: allMatching && !small ? 'Remove All' : 'Remove',
				undoable: n > 1
			};
			return;
		}

		this.confirm = {
			action,
			rows: [...rows],
			header: this.labels.addDialogHeader,
			message:
				`${this.phrase('add', n)}?` +
				(allMatching ? ` This grants access to every ${this.labels.singular} matching your search.` : ''),
			items: [],
			acceptLabel: allMatching ? 'Add All' : 'Add',
			undoable: n > 1
		};
	}

	private startCountdown(): void {
		this.confirmPhase = 'countdown';
		this.secondsLeft = this.undoSeconds;
		this.releasePending = this.pendingChanges.register(this.pendingChange);
		this.countdownTimer = setInterval(() => {
			// Paused while the "leave this page?" prompt is open, so it can't send behind the user's back.
			if (this.leavePromptVisible) return;
			this.secondsLeft--;
			if (this.secondsLeft <= 0) {
				this.stopCountdown();
				this.sendConfirmed();
			}
		}, 1000);
	}

	private stopCountdown(): void {
		if (this.countdownTimer) clearInterval(this.countdownTimer);
		this.countdownTimer = null;
		this.releasePending?.();
		this.releasePending = null;
	}

	/** Drops an unsent batch: nothing is sent. */
	private discardPending(): void {
		if (this.confirmPhase === 'countdown') {
			this.stopCountdown();
			this.closeConfirm();
		}
		this.answerLeave(true);
	}

	private promptLeave(): Promise<boolean> {
		this.leavePromptVisible = true;
		return new Promise<boolean>((resolve) => (this.resolveLeave = resolve));
	}

	private answerLeave(leave: boolean): void {
		this.leavePromptVisible = false;
		const resolve = this.resolveLeave;
		this.resolveLeave = null;
		resolve?.(leave);
	}

	private closeConfirm(): void {
		this.confirm = null;
		this.confirmPhase = 'confirm';
		this.confirmError = '';
	}

	/** The one bulk request for an accepted confirm. The dialog stays open until it answers. */
	private sendConfirmed(): void {
		const confirm = this.confirm;
		if (!confirm) return;
		this.confirmPhase = 'sending';
		this.confirmError = '';
		this.busy = true;

		this.subscriptions.add(
			this.call(confirm.action, this.idsOf(confirm.rows)).subscribe({
				next: (result) => {
					this.closeConfirm();
					this.onBatchDone(confirm.action, result);
				},
				error: (error: unknown) => {
					this.busy = false;
					this.confirmPhase = 'failed';
					const n = confirm.rows.length;
					const reason = this.serverMessage(error);
					this.confirmError =
						`Couldn't ${this.lowerFirst(this.phrase(confirm.action, n))}. Nothing was changed.` +
						(reason ? ` ${reason}` : '');
				}
			})
		);
	}

	/** Small adds: no confirm, sent straight away. */
	private runBatch(action: BulkAccessAction, ids: BulkAccessId[]): void {
		if (!ids.length) return;
		this.busy = true;

		this.subscriptions.add(
			this.call(action, ids).subscribe({
				next: (result) => this.onBatchDone(action, result),
				error: () => {
					this.toastError(`Failed to ${action} access. The list has been refreshed.`);
					// Show what the server actually holds after a failed call.
					this.reload();
				}
			})
		);
	}

	private call(action: BulkAccessAction, ids: BulkAccessId[]) {
		return action === 'add' ? this.dataSource.add(ids) : this.dataSource.remove(ids);
	}

	private onBatchDone(action: BulkAccessAction, result: BulkAccessResult): void {
		const changedIds = result.changedIds ?? [];
		if (action === 'add') this.addDialogVisible = false;
		this.clearSelection();
		this.toastResult(action, changedIds.length);
		this.changed.emit({ action, changedIds });
		this.reload();
	}

	/**
	 * Quiet reload: no skeleton after the first load — the rows stay under the table's loading
	 * overlay until the fresh list arrives. p-table resets `first` when its value changes under
	 * an active sort, so the page is restored (and stepped back if it no longer exists).
	 */
	private reload(): void {
		this.busy = true;
		const keepFirst = this.first;

		this.subscriptions.add(
			this.dataSource.list().subscribe({
				next: (rows) => {
					this.rows = rows;
					this.applyFilters();
					this.initialLoading = false;
					this.busy = false;
					setTimeout(() => (this.first = this.clampFirst(keepFirst)));
				},
				error: () => {
					this.initialLoading = false;
					this.busy = false;
					this.toastError(`Failed to load ${this.labels.plural}.`);
				}
			})
		);
	}

	private applyFilters(): void {
		const active = Object.entries(this.filters)
			.map(([field, value]) => [field, (value ?? '').trim().toLowerCase()] as const)
			.filter(([, value]) => value !== '');

		this.filteredRows = active.length
			? this.rows.filter((row) =>
					active.every(([field, value]) => {
						const col = this.columns.find((c) => c.field === field);
						const text = col ? this.cellText(row, col) : String(row[field] ?? '');
						return text.toLowerCase().includes(value);
					})
				)
			: [...this.rows];
	}

	private clampFirst(first: number): number {
		const total = this.filteredRows.length;
		if (first < total) return first;
		return total === 0 ? 0 : Math.floor((total - 1) / this.pageSize) * this.pageSize;
	}

	private isSelected(row: T): boolean {
		const id = row[this.idField];
		return this.selection.some((selected) => selected[this.idField] === id);
	}

	private idsOf(rows: T[]): BulkAccessId[] {
		return rows.map((row) => Number(row[this.idField]));
	}

	private noun(n: number): string {
		return n === 1 ? this.labels.singular : this.labels.plural;
	}

	private phrase(action: BulkAccessAction, n: number): string {
		const key = action === 'add' ? 'add' : 'remove';
		return (this.labels.phrases?.[key] ?? DEFAULT_PHRASES[key])(n, this.noun(n));
	}

	private pastPhrase(action: BulkAccessAction, n: number): string {
		const key = action === 'add' ? 'added' : 'removed';
		return (this.labels.phrases?.[key] ?? DEFAULT_PHRASES[key])(n, this.noun(n));
	}

	private lowerFirst(text: string): string {
		return text.charAt(0).toLowerCase() + text.slice(1);
	}

	private serverMessage(error: unknown): string {
		const body = error instanceof HttpErrorResponse ? error.error : null;
		const message = typeof body?.message === 'string' ? body.message.trim() : '';
		return message ? `The server said: ${message}` : '';
	}

	private toastResult(action: BulkAccessAction, n: number): void {
		this.messageService.clear(this.toastKey);
		this.messageService.add({
			key: this.toastKey,
			severity: n === 0 ? 'info' : 'success',
			detail: n === 0 ? 'No changes were needed.' : `${this.pastPhrase(action, n)}.`,
			life: 4000
		});
	}

	private toastError(detail: string): void {
		this.messageService.add({ key: this.toastKey, severity: 'error', detail, life: 6000 });
	}
}
